import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { CoreService } from '../../core/services/core.service';
import { SubscriptionTier } from '../../core/services/billing.service';
import { PLAN_CATALOG, PlanCard } from '../../core/plan-catalog';

type SelectablePlan = PlanCard & { highlighted: boolean };

@Component({
  selector: 'app-select-plan',
  templateUrl: './select-plan.component.html',
})
/**
 * Signup plan selection.
 *
 * Every path here activates the FREE tier first. That is deliberate: the org is
 * bootstrapped by POST /api/v1/subscribe, and Stripe Checkout is org-scoped, so
 * there is no organization to bill until that call succeeds. Choosing a paid
 * plan therefore creates the free account and then carries the upgrade intent to
 * the billing page, which starts Checkout. `/subscribe` itself refuses paid
 * tiers, so the intent can only be honoured through Stripe.
 *
 * CUSTOM is absent by design — it is admin-assigned, not self-selectable.
 */
export class SelectPlanComponent {
  loading = false;
  error: string | null = null;
  activatingTier: SubscriptionTier | null = null;

  readonly plans: SelectablePlan[] = PLAN_CATALOG
    .filter(p => p.listed)
    .map(p => ({ ...p, highlighted: p.tier === 'PRO' }));

  constructor(
    private readonly core: CoreService,
    private readonly router: Router,
  ) {}

  selectPlan(tier: SubscriptionTier): void {
    if (this.loading) return;

    this.loading = true;
    this.error = null;
    this.activatingTier = tier;

    // Always activate FREE: this is what creates the user's organization, and
    // a paid tier here would be rejected by the server anyway.
    this.core.subscribe('FREE').subscribe({
      next: () => {
        if (tier === 'PRO' || tier === 'ENTERPRISE') {
          // Hand the upgrade intent to the billing page, which starts Checkout
          // now that an org exists to bill.
          this.router.navigate(['/settings/billing'], { queryParams: { upgrade: tier } });
        } else {
          this.router.navigate(['/']);
        }
      },
      error: (err: { error?: { message?: string } }) => {
        this.error = err?.error?.message ?? 'Failed to activate plan. Please try again.';
        this.loading = false;
        this.activatingTier = null;
      },
    });
  }
}
